import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, StyleSheet } from 'react-native';

import Clipboard from '@react-native-clipboard/clipboard';
import { useNavigation } from '@react-navigation/native';

import AlertTriangleWhite from '@/assets/icons/alertTriangleWhite';
import VoidedProgressDot from '@/assets/icons/voided-progress-dot';
import { BetDetailShell } from '@/components/BetDetailShell';
import { Button } from '@/components/ButtonComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { Banner } from '@/feature/betslip-sbk/components/Banner';
import { getConfirmedPositionSummary } from '@/feature/betslip-sbk/utils/prediction-position';
import type { EntryCashOut } from '@/hooks/use-entry-cash-out';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { useShowPositionSummarySheet } from '@/hooks/use-show-position-summary-sheet';
import { designSystem, withOpacity } from '@/styles/styles';
import { formatMatchupString } from '@/utils/sport-utils';
import { format } from 'date-fns';

import { Bet } from '../hooks/types';
import { useIsUnmatchedPrediction } from '../hooks/use-is-unmatched-prediction';
import { useSbkBetDetailPromos } from '../hooks/use-sbk-bet-detail-promos';
import { useSbkEntryCashOut } from '../hooks/use-sbk-entry-cash-out';
import { classifyBetVariant } from '../utils/classify-bet-variant';
import { isVoidedResult } from '../utils/is-voided-result';
import { getBuyFill } from '../utils/prediction-cost';
import { getProgressDots } from '../utils/utils';
import { EventGrouping } from './EventGrouping';
import { BetDetailCashOutFooter } from './bet-details/BetDetailCashOutFooter';
import { BetSummarySection } from './bet-details/BetSummarySection';

const useBetTitle = (bet: Bet) => {
    const { t } = useTranslation('bets');
    const isPredictions = useIsPredictionsProduct();
    const count = { count: bet.selection_count };
    switch (classifyBetVariant(bet)) {
        case 'SINGLE': {
            const event = bet.events[0];
            const matchup = formatMatchupString(
                event.home_team.short_code,
                event.away_team.short_code,
                event.sport?.name ?? ''
            );
            return `${matchup} ${isPredictions ? t('position') : t('single')}`;
        }
        case 'PARLAY':
            return t('parlay_bet_name', count);
        case 'SGP':
            return t('sgp_bet_name', count);
        case 'SGP+':
            return t('sgp_plus_bet_name', count);
    }
};

export const formatDate = (dateString: number | null): string =>
    dateString ? format(new Date(dateString), 'Pp') : '--';

const useBetFooter = (bet: Bet) => {
    const { t } = useTranslation(['bets', 'bet_status']);
    const isPredictions = useIsPredictionsProduct();
    const resultKey = isPredictions && bet.result === 'CASHEDOUT' ? 'SOLD' : bet.result; // Predictions positions are sold, not cashed out; the backend result stays CASHEDOUT.
    return [
        {
            label: `${isPredictions ? t('bets:position_id') : t('bets:entry_id')}:`,
            value: bet.id,
            testID: 'bet-id',
            onLongPress: () => {
                Clipboard.setString(bet.id);
                Alert.alert(t(isPredictions ? 'bets:position_id_copied' : 'bets:entry_id_copied'), bet.id);
            },
        },
        {
            label: `${t('placed')}:`,
            value: formatDate(bet.created_at),
        },
        {
            label: `${t('settled')}:`,
            value: bet.settled_at ? formatDate(bet.settled_at) : '--',
        },
        {
            label: `${t('result')}:`,
            value: bet.result !== 'PENDING' ? t(`bet_status:${resultKey}`) : '--',
        },
    ];
};

const gradientColors = (color: string) => {
    return [withOpacity(color, 0.15), withOpacity(color, 0)];
};

const useBetDetail = (bet: Bet, cashOut: EntryCashOut) => {
    // Only the confirmed phase touches the header: green "Payout" at the cashed-out amount (the settled
    // default reads "Paid"), covering the refetch-failure fallback where the cached bet is still open.
    const cashedOutSummary =
        cashOut.status === 'cashedOut' && cashOut.payout
            ? {
                  header3Text: cashOut.payout.payoutLabel,
                  header3TextColor: ds.colorToken.text.success,
                  body3Text: cashOut.payout.payoutAmount,
                  body3TextColor: ds.colorToken.text.success,
              }
            : undefined;
    return {
        title: useBetTitle(bet),
        progressDots: getProgressDots(bet),
        summary: <BetSummarySection bet={bet} betSummarySectionProps={cashedOutSummary} />,
        footer: useBetFooter(bet),
        gradientColor: gradientColors(ds.colorValue.background.brand.emphasis), // Keep top gradient purple for every scenarios for now
        hasActionTray: cashOut.controls != null,
        shouldShowProgressDots: bet.bet_type !== 'SINGLE',
        promo: useSbkBetDetailPromos(bet),
    };
};

const usePositionSummaryButton = (bet: Bet) => {
    const { t } = useTranslation('betslip_sbk');
    const isPredictions = useIsPredictionsProduct();
    const showPositionSummarySheet = useShowPositionSummarySheet();
    if (!isPredictions) {
        return null;
    }
    const buyFill = getBuyFill(bet);
    const confirmed = buyFill ? getConfirmedPositionSummary(buyFill) : null;
    // No estimates on bet details: the button stays disabled until the order fills report the actual costs.
    const actualSummary = confirmed ? { ...confirmed, variant: 'actual' as const } : undefined;
    return (
        <Button
            label={t('position_summary')}
            hierarchy={'subtle'}
            disabled={!actualSummary}
            disableTextStyle={styles.disabledButtonText}
            onPress={actualSummary ? () => showPositionSummarySheet(actualSummary) : undefined}
        />
    );
};

type SbkBetDetailProps = {
    bet: Bet;
    isRefreshing?: boolean;
    onRefresh?: () => void;
};

export const SbkBetDetail = ({ bet, isRefreshing, onRefresh }: SbkBetDetailProps) => {
    const cashOut = useSbkEntryCashOut(bet);
    const isPredictions = useIsPredictionsProduct();
    const betDetailShellProps = useBetDetail(bet, cashOut);
    const positionSummaryButton = usePositionSummaryButton(bet);
    const isVoided = isVoidedResult(bet.result);
    const isUnmatched = useIsUnmatchedPrediction(bet);
    const { t } = useTranslation('bets');
    const navigation = useNavigation();

    const onSharePress = () => {
        navigation.navigate('BetShareScreen', { betId: bet.id, bet });
    };

    return (
        <>
            <BetDetailShell
                isRefreshing={isRefreshing}
                onRefresh={onRefresh}
                onSharePress={onSharePress}
                secondaryButton={positionSummaryButton}
                {...betDetailShellProps}
            >
                {isVoided ? (
                    <Banner
                        leftIcon={<VoidedProgressDot width={20} height={20} fill={ds.colorValue.icon.primary} />}
                        message={isPredictions ? t('trade_voided_warning') : t('entry_voided_warning')}
                        backgroundColor={ds.colorToken.background.neutral.subtler}
                    />
                ) : null}
                {isUnmatched ? (
                    <Banner
                        leftIcon={<AlertTriangleWhite width={24} height={24} />}
                        message={t('trade_unconfirmed_warning')}
                        backgroundColor={ds.colorToken.background.neutral.subtler}
                    />
                ) : null}
                <Box alignItems="center">
                    <EventGrouping bet={bet} />
                </Box>
            </BetDetailShell>
            {cashOut.controls ? <BetDetailCashOutFooter controls={cashOut.controls} /> : null}
        </>
    );
};

const styles = StyleSheet.create({
    disabledButtonText: {
        color: designSystem.colors.neutral400,
    },
});
